import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, ArrowLeft, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const HERO = "https://images.unsplash.com/photo-1512100356356-de1b84283e18?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTF8MHwxfHNlYXJjaHwyfHxwcml2YXRlJTIwdHJhdmVsJTIwY2x1YnxlbnwwfHx8fDE3ODM2NDMyMjF8MA&ixlib=rb-4.1.0&q=85";

const ForgotPassword = () => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send reset link");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[var(--ir-bg)]" data-testid="forgot-password-page">
      <div className="hidden lg:block relative">
        <img src={HERO} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[var(--ir-blue-dark)]/70 via-[var(--ir-blue-dark)]/20 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <h2 className="font-display text-4xl leading-tight text-white">Locked out?<br /><span className="text-gradient-orange italic">We'll get you back in.</span></h2>
        </div>
      </div>

      <div className="flex items-center justify-center px-6 py-16">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/"><Logo size="md" /></Link>

          {sent ? (
            <div className="mt-10" data-testid="forgot-success">
              <div className="flex items-center gap-3">
                <CheckCircle2 className="w-8 h-8 text-[var(--ir-orange)]" />
                <h1 className="font-display text-3xl text-[var(--ir-text)]">Check your inbox</h1>
              </div>
              <p className="text-[var(--ir-muted)] mt-4 leading-relaxed">
                If an account exists for <strong className="text-[var(--ir-text)]">{email}</strong>, we've sent a secure link to reset your password. The link expires in 60 minutes.
              </p>
              <Button asChild className="mt-8 bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12 px-8" data-testid="forgot-back-to-login-btn">
                <Link to="/login"><ArrowLeft className="w-4 h-4 mr-2" />Back to login</Link>
              </Button>
            </div>
          ) : (
            <>
              <h1 className="font-display text-4xl mt-10 text-[var(--ir-text)]">Forgot password</h1>
              <p className="text-[var(--ir-muted)] mt-2">Enter your email and we'll send you a reset link.</p>

              <form onSubmit={submit} className="mt-8 space-y-5">
                <div>
                  <Label htmlFor="email" className="text-[var(--ir-text)]">Email</Label>
                  <Input id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" className="mt-2 bg-white border-[var(--ir-border)] rounded-none h-12 focus-visible:ring-[var(--ir-blue)]" data-testid="forgot-email-input" />
                </div>
                <Button type="submit" disabled={loading} className="w-full bg-[var(--ir-orange)] hover:bg-[var(--ir-orange-soft)] text-white font-semibold rounded-none h-12" data-testid="forgot-submit-btn">
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Mail className="w-4 h-4 mr-2" />Send reset link</>}
                </Button>
              </form>

              <p className="text-[var(--ir-muted)] mt-8 text-sm">
                <Link to="/login" className="text-[var(--ir-blue)] font-medium hover:underline" data-testid="forgot-to-login-link">Back to login</Link>
              </p>
            </>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default ForgotPassword;
